"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Sun, Coffee, Moon } from "lucide-react";

const DAYPARTS = [
  {
    icon: Coffee,
    label: { en: "Breakfast", es: "Desayuno" },
    time: "7-11 AM",
    items: ["Chilaquiles · $7", "Migas · $8", "Café · $3"],
    color: "#FB923C",
  },
  {
    icon: Sun,
    label: { en: "Lunch", es: "Almuerzo" },
    time: "11 AM-5 PM",
    items: ["Birria Plate · $12", "Tacos · $4 ea", "Agua fresca · $4"],
    color: "#F4722B",
  },
  {
    icon: Moon,
    label: { en: "Dinner", es: "Cena" },
    time: "5-10 PM",
    items: ["Birria Ramen · $18", "Quesabirria 3pc · $14", "Margarita · $9"],
    color: "#D7263D",
  },
];

export default function MenuBoardsDaypartScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-white overflow-hidden">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
            {t("Dayparted menus", "Menús por daypart")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal">
            {t("Breakfast. Lunch. Dinner. ", "Desayuno. Almuerzo. Cena. ")}
            <span className="text-redy-charcoal/30">{t("By itself.", "Solos.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mt-6">
            {t(
              "Set the schedule once. At 11:00 AM the breakfast menu fades out and the lunch menu fades in. At 5:00 PM dinner takes over. The screen does it for you. No staff has to remember to swap slides.",
              "Configuras el horario una vez. A las 11:00 AM el desayuno se desvanece y aparece el almuerzo. A las 5:00 PM toma el dinner. La pantalla lo hace sola. Nadie tiene que acordarse de cambiar slides."
            )}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-[1080px] mx-auto"
        >
          {DAYPARTS.map((dp, i) => {
            const Icon = dp.icon;
            return (
              <motion.div
                key={dp.label.en}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: 0.1 * i }}
                className="relative rounded-[24px] overflow-hidden aspect-[3/4] flex flex-col"
                style={{
                  background: `linear-gradient(180deg, ${dp.color} 0%, #1A1A1A 110%)`,
                }}
              >
                <div
                  className="absolute inset-0 opacity-40 pointer-events-none"
                  style={{
                    backgroundImage:
                      "radial-gradient(circle at 30% 25%, rgba(255,255,255,0.4) 0%, transparent 50%)",
                  }}
                />

                <div className="relative flex-1 flex flex-col p-6 text-white">
                  <div className="flex items-center gap-2.5 mb-1.5">
                    <div className="w-9 h-9 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <div className="text-[10px] font-black uppercase tracking-widest text-white/80">
                        {dp.time}
                      </div>
                      <div className="text-[18px] font-black tracking-tight leading-none">
                        {t(dp.label.en, dp.label.es)}
                      </div>
                    </div>
                  </div>

                  <div className="mt-auto space-y-1">
                    {dp.items.map((item) => (
                      <div
                        key={item}
                        className="bg-white/[0.10] backdrop-blur-sm border border-white/10 rounded-[8px] px-3 py-2"
                      >
                        <div className="text-[12px] font-black text-white">{item}</div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 text-[9px] font-bold uppercase tracking-widest text-white/65">
                    {t("Auto-active", "Auto-activo")} {dp.time}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center mt-10"
        >
          <div className="inline-flex items-center gap-2 bg-[#FAFAF8] border border-black/5 px-4 py-2.5 rounded-full">
            <span className="text-[12px] font-bold text-redy-charcoal">
              {t("Swap times once. Auto-switches every day.", "Configuras los tiempos una vez. Se cambia automático cada día.")}
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
